import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'build', label: 'Build & Insert' },
  { key: 'search', label: 'Search & StartsWith' },
  { key: 'wildcard', label: 'Wildcard Search' },
  { key: 'autocomplete', label: 'Autocomplete' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

/* ─── Trie helpers ───────────────────────────────────────────────── */
const emptyTrie = () => ({ children: {}, isEnd: false });
const insertTrie = (node, word, i = 0) => {
  const copy = { children: { ...node.children }, isEnd: node.isEnd };
  if (i === word.length) { copy.isEnd = true; return copy; }
  const ch = word[i];
  const child = copy.children[ch] || emptyTrie();
  copy.children[ch] = insertTrie(child, word, i + 1);
  return copy;
};
const walkTo = (node, prefix) => {
  let cur = node;
  for (const ch of prefix) {
    if (!cur.children[ch]) return null;
    cur = cur.children[ch];
  }
  return cur;
};
const collectWords = (node, prefix, out) => {
  if (node.isEnd) out.push(prefix);
  Object.entries(node.children).forEach(([ch, child]) => collectWords(child, prefix + ch, out));
};

/* ─── Trie view with path-based highlighting ─────────────────────── */
const TrieView = ({ trie, highlight }) => {
  const Node = ({ node, ch, path }) => {
    const active = highlight && highlight.includes(path);
    const children = Object.entries(node.children);
    return (
      <div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
          minWidth: 38, height: 38, padding: '0 9px', borderRadius: 10,
          background: active ? `linear-gradient(135deg,${V1},${V2})` : 'var(--bg3)',
          border: `1.5px solid ${active ? V1 : 'var(--border)'}`,
          color: active ? '#fff' : 'var(--text)', fontWeight: 800, fontSize: 14,
          fontFamily: "'Fira Code','Consolas',monospace",
        }}>
          {ch}
          {node.isEnd && <span style={{ position: 'absolute', bottom: -5, right: -5, width: 11, height: 11, borderRadius: '50%', background: GREEN, border: '2px solid var(--bg2)' }} />}
        </span>
        {children.length > 0 && (
          <div style={{ marginLeft: 18, paddingLeft: 16, marginTop: 10, borderLeft: '2px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {children.map(([c, child]) => <Node key={c} node={child} ch={c} path={path + c} />)}
          </div>
        )}
      </div>
    );
  };
  return (
    <div style={{ overflowX: 'auto', paddingBottom: 10 }}>
      <Node node={trie} ch="•" path="" />
    </div>
  );
};

const smallInput = {
  width: 130, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

const DEFAULT_WORDS = ['cat', 'car', 'card', 'care', 'dog'];
const buildDefault = () => DEFAULT_WORDS.reduce((t, w) => insertTrie(t, w), emptyTrie());

/* ─── Build & Insert ─────────────────────────────────────────────── */
const BuildMode = () => {
  const [trie, setTrie] = useState(buildDefault());
  const [value, setValue] = useState('');
  const [lastPath, setLastPath] = useState(null);

  const insert = () => {
    const w = value.trim().toLowerCase();
    if (!w) return;
    setTrie(t => insertTrie(t, w));
    setLastPath([...Array(w.length + 1)].map((_, i) => w.slice(0, i)));
    setValue('');
  };
  const reset = () => { setTrie(emptyTrie()); setLastPath(null); };

  const code = {
    lines: [
      'class TrieNode {',
      '    Map<Character, TrieNode> children = new HashMap<>();',
      '    boolean isEnd = false;',
      '}',
      '',
      'void insert(String word) {',
      '    TrieNode curr = root;',
      '    for (char c : word.toCharArray()) {',
      '        curr = curr.children.computeIfAbsent(c, k -> new TrieNode());',
      '    }',
      '    curr.isEnd = true;',
      '}',
    ],
    active: lastPath ? 8 : -1,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <TrieView trie={trie} highlight={lastPath} />
        <p style={{ fontSize: 11, color: 'var(--muted)', margin: '4px 0 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: GREEN }} /> = end of a complete word
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={value} onChange={e => setValue(e.target.value)} style={smallInput} placeholder="word" />
            <button onClick={insert} style={smallBtn(V1)}>Insert</button>
            <button onClick={reset} style={smallBtn(RED)}>Clear</button>
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
            Insert a word — shared prefixes (like "car" inside "card" and "care") reuse the same existing nodes instead of duplicating them.
          </p>
        </div>
      </div>
      <CodePanel title="Java — Trie Insertion" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Search & StartsWith ────────────────────────────────────────── */
const SearchMode = () => {
  const [trie] = useState(buildDefault());
  const [word, setWord] = useState('care');
  const [mode, setMode] = useState('search');
  const [result, setResult] = useState(null);

  const run = () => {
    const w = word.trim().toLowerCase();
    if (!w) return;
    const path = [];
    let cur = trie;
    let found = true;
    for (const ch of w) {
      if (!cur.children[ch]) { found = false; break; }
      cur = cur.children[ch];
      path.push(path.length === 0 ? ch : path[path.length - 1] + ch);
    }
    const fullPath = ['', ...path];
    if (!found) { setResult({ ok: false, reason: 'missing-path', path: fullPath }); return; }
    if (mode === 'search' && !cur.isEnd) { setResult({ ok: false, reason: 'not-end', path: fullPath }); return; }
    setResult({ ok: true, path: fullPath });
  };

  const code = {
    lines: mode === 'search' ? [
      'boolean search(String word) {',
      '    TrieNode node = walk(word);',
      '    return node != null && node.isEnd;',
      '}',
    ] : [
      'boolean startsWith(String prefix) {',
      '    return walk(prefix) != null;',
      '}',
    ],
    active: mode === 'search' ? 2 : 1,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <TrieView trie={trie} highlight={result ? result.path : null} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => { setMode('search'); setResult(null); }} style={{ ...smallBtn(mode === 'search' ? V1 : 'var(--bg3)'), color: mode === 'search' ? '#fff' : 'var(--muted)' }}>search()</button>
            <button onClick={() => { setMode('starts'); setResult(null); }} style={{ ...smallBtn(mode === 'starts' ? V1 : 'var(--bg3)'), color: mode === 'starts' ? '#fff' : 'var(--muted)' }}>startsWith()</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={word} onChange={e => { setWord(e.target.value); setResult(null); }} style={smallInput} />
            <button onClick={run} style={smallBtn(GREEN)}>Run</button>
          </div>
          {result && (
            <div style={{
              fontSize: 13, fontWeight: 700, padding: '10px 14px', borderRadius: 9,
              background: result.ok ? 'rgba(46,204,113,0.12)' : 'rgba(240,65,85,0.1)', color: result.ok ? '#1e9e57' : RED,
            }}>
              {result.ok ? `true — the full path exists${mode === 'search' ? ' and ends exactly at a complete word' : ''}.` :
                result.reason === 'missing-path' ? 'false — the path breaks partway down; that character sequence was never inserted.' :
                'false — the path exists, but no word ends exactly there (it\'s only a prefix of something longer).'}
            </div>
          )}
        </div>
      </div>
      <CodePanel title="Java — search() vs startsWith()" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Wildcard Search ────────────────────────────────────────────── */
const WildcardMode = () => {
  const [trie] = useState(buildDefault());
  const [pattern, setPattern] = useState('ca.');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const p = pattern.trim().toLowerCase();
    if (!p) return;
    const built = [];
    let matched = false;
    const dfs = (node, i, path) => {
      if (matched) return;
      if (i === p.length) { if (node.isEnd) { built.push({ path, note: `Reached the end of the pattern and this node IS a complete word — match!`, match: true }); matched = true; } return; }
      const c = p[i];
      if (c === '.') {
        built.push({ path, note: `'.' at position ${i} — try every child of this node.` });
        for (const [ch, child] of Object.entries(node.children)) {
          if (matched) break;
          built.push({ path: path + ch, note: `Trying branch '${ch}'...` });
          dfs(child, i + 1, path + ch);
        }
      } else {
        if (!node.children[c]) { built.push({ path, note: `Need '${c}' next, but no such child exists — dead end on this branch.` }); return; }
        built.push({ path: path + c, note: `'${c}' at position ${i} — follow that specific child.` });
        dfs(node.children[c], i + 1, path + c);
      }
    };
    dfs(trie, 0, '');
    if (!matched) built.push({ done: true, match: false });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;

  const code = {
    lines: [
      'boolean search(TrieNode node, String pattern, int i) {',
      '    if (i == pattern.length()) return node.isEnd;',
      '    char c = pattern.charAt(i);',
      '    if (c == \'.\') {',
      '        for (TrieNode child : node.children.values()) {',
      '            if (search(child, pattern, i + 1)) return true;',
      '        }',
      '        return false;',
      '    }',
      '    TrieNode child = node.children.get(c);',
      '    return child != null && search(child, pattern, i + 1);',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 1 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <TrieView trie={trie} highlight={cur && !cur.done ? [cur.path] : null} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Pattern</span>
            <input value={pattern} onChange={e => { setPattern(e.target.value); setSteps(null); }} style={smallInput} placeholder="ca." />
            <button onClick={run} style={smallBtn(V1)}>Search</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
            {!cur ? 'Use "." to mean any single character, then Search.' :
              cur.done ? (cur.match ? 'Match found.' : 'No branch ever reached a complete word — no match.') : cur.note}
          </p>
        </div>
      </div>
      <CodePanel title="Java — Wildcard DFS" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Autocomplete ───────────────────────────────────────────────── */
const AutocompleteMode = () => {
  const [trie] = useState(buildDefault());
  const [prefix, setPrefix] = useState('ca');

  const p = prefix.trim().toLowerCase();
  const prefixNode = p ? walkTo(trie, p) : trie;
  const suggestions = [];
  if (prefixNode) collectWords(prefixNode, p, suggestions);

  const code = {
    lines: [
      'List<String> autocomplete(String prefix) {',
      '    TrieNode node = walk(prefix);',
      '    List<String> results = new ArrayList<>();',
      '    if (node != null) collect(node, prefix, results);',
      '    return results;',
      '}',
      '',
      'void collect(TrieNode node, String path, List<String> results) {',
      '    if (node.isEnd) results.add(path);',
      '    for (var entry : node.children.entrySet()) {',
      '        collect(entry.getValue(), path + entry.getKey(), results);',
      '    }',
      '}',
    ],
    active: 3,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <TrieView trie={trie} highlight={prefixNode ? [p] : null} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Prefix</span>
            <input value={prefix} onChange={e => setPrefix(e.target.value)} style={smallInput} />
          </div>
          <div>
            {suggestions.length === 0 ? <span style={{ fontSize: 13, color: 'var(--muted)' }}>No words start with "{p}".</span> :
              suggestions.map(w => <Chip key={w} color={GREEN}>{w}</Chip>)}
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0 }}>
            Walk straight to the prefix's node — one path down — then DFS only THAT subtree. The rest of the trie is never touched.
          </p>
        </div>
      </div>
      <CodePanel title="Java — Prefix Autocomplete" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const QUIZ_TRIE = buildDefault();
const QUIZ_CANDIDATES = ['cat', 'car', 'ca', 'care', 'dog', 'do', 'cart', 'card'];
const buildQuizScenario = () => {
  const word = QUIZ_CANDIDATES[Math.floor(Math.random() * QUIZ_CANDIDATES.length)];
  const node = walkTo(QUIZ_TRIE, word);
  const correct = node !== null && node.isEnd;
  return { word, correct };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  return (
    <div>
      <TrieView trie={QUIZ_TRIE} />
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '14px 0' }}>
        Does search("{scenario.word}") return true?
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {[{ key: true, label: 'True' }, { key: false, label: 'False' }].map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={String(o.key)} onClick={() => pick(o.key)} disabled={revealed} style={{
              padding: '10px 20px', borderRadius: 10, border: `1.5px solid ${border}`, background: bg, color,
              fontSize: 13.5, fontWeight: 700, cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const TriePlayground = () => {
  const [mode, setMode] = useState('build');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'build' && <BuildMode />}
      {mode === 'search' && <SearchMode />}
      {mode === 'wildcard' && <WildcardMode />}
      {mode === 'autocomplete' && <AutocompleteMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default TriePlayground;
